import React, { Fragment, useRef, useState } from "react";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import { Button, Modal } from "react-bootstrap";
import fontkit from "@pdf-lib/fontkit";
import { PDFDocument } from "pdf-lib";

import { db, storage } from "../../../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import {
  where,
  query,
  collection,
  doc,
  updateDoc,
  deleteField,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";
import { usePagination } from "../../../hook/usePagination";
import ExternalRepairItem from "./ExternalRepairItem";
import { ExternalRepairForm } from "./ExternalRepairForm";

const ExternalRepairList = () => {
  const { currentUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editTicket, setEditTicket] = useState(null);
  const [searchItem, setSearchItem] = useState("");

  // Image viewing states
  const [imageModal, setImageModal] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const searchRef = useRef(null);
  const externalRepairInputRef = useRef({
    vendor: "",
    sentDate: "",
    details: "",
    notes: "",
    repairStatus: "",
  });

  const itemPerPage = 50;

  // Query for tickets with external repair
  let queryRef = query(
    collection(db, "serviceTickets"),
    where("hasExternalRepair", "==", true),
  );

  const {
    items: externalRepairList,
    isStart,
    isEnd,
    getPrev,
    getNext,
    totalItems,
    totalPages,
    currentPage,
    goToPage,
  } = usePagination(queryRef, { limit: itemPerPage });

  const onSearchChange = () => {
    setSearchItem(searchRef.current.value);
  };

  const handleOnEdit = (id) => {
    const ticket = externalRepairList.find((item) => item.id === id);
    setEditTicket(ticket);
    // Reset the input ref with existing data
    const extRepair =
      ticket && ticket.externalRepair ? ticket.externalRepair : {};
    externalRepairInputRef.current = {
      vendor: extRepair.vendor || "",
      sentDate: extRepair.sentDate || "",
      details: extRepair.details || "",
      notes: extRepair.notes || "",
      repairStatus: extRepair.repairStatus || "",
    };
    setShowModal(true);
  };

  const handleChange = (name) => (e) => {
    externalRepairInputRef.current[name] = e.target.value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editTicket) {
      swal("Error", "No ticket selected", "error");
      return;
    }

    // Validate required fields
    if (!externalRepairInputRef.current.vendor) {
      swal("Error", "กรุณากรอกชื่อบริษัทที่ส่งซ่อม", "error");
      return;
    }
    if (!externalRepairInputRef.current.sentDate) {
      swal("Error", "กรุณากรอกวันที่ส่งบริษัทนอก", "error");
      return;
    }
    if (!externalRepairInputRef.current.repairStatus) {
      swal("Error", "กรุณาเลือกสถานะ", "error");
      return;
    }

    try {
      const repairData = {
        externalRepair: {
          externalRepairNo:
            (editTicket &&
              editTicket.externalRepair &&
              editTicket.externalRepair.externalRepairNo) ||
            "",
          vendor: externalRepairInputRef.current.vendor,
          sentDate: externalRepairInputRef.current.sentDate,
          details: externalRepairInputRef.current.details || "",
          notes: externalRepairInputRef.current.notes || "",
          repairStatus: externalRepairInputRef.current.repairStatus,
          createdAt:
            (editTicket &&
              editTicket.externalRepair &&
              editTicket.externalRepair.createdAt) ||
            new Date(),
          createdBy:
            (editTicket &&
              editTicket.externalRepair &&
              editTicket.externalRepair.createdBy) ||
            currentUser.displayName,
          updatedAt: new Date(),
          updatedBy: currentUser.displayName,
        },
        updatedAt: new Date(),
        updatedBy: currentUser.displayName,
      };

      // Update service status based on repair status
      if (externalRepairInputRef.current.repairStatus === "เสร็จสิ้น") {
        repairData.serviceStatus = "เสร็จสิ้น";
      } else if (externalRepairInputRef.current.repairStatus === "ยกเลิก") {
        repairData.serviceStatus = "ลูกค้าไม่อนุมัติ";
      }

      await updateDoc(doc(db, "serviceTickets", editTicket.id), repairData);

      handleOnHide();
      swal("Success", "บันทึกข้อมูลเรียบร้อย", "success");
    } catch (error) {
      console.error("Error updating external repair:", error);
      swal("Error", "Failed to update external repair data", "error");
    }
  };

  const handleOnHide = () => {
    setShowModal(false);
    setEditTicket(null);
    externalRepairInputRef.current = {
      vendor: "",
      sentDate: "",
      details: "",
      notes: "",
      repairStatus: "",
    };
  };

  const handleOnViewFile = (id, file) => {
    setIsImageLoading(true);
    getDownloadURL(ref(storage, "serviceImage/" + id + "/" + file)).then(
      (url) => {
        setFileUrl(url);
        setIsImageLoading(false);
        setImageModal(true);
      },
    );
  };

  const handleCloseImageModal = () => {
    setImageModal(false);
    setFileUrl(null);
  };

  const handleOnDelete = async (id) => {
    swal({
      title: "ยืนยันการลบ?",
      text: "ต้องการลบข้อมูลส่งซ่อมบริษัทนอกหรือไม่?",
      icon: "warning",
      buttons: ["ยกเลิก", "ลบ"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await updateDoc(doc(db, "serviceTickets", id), {
            hasExternalRepair: false,
            externalRepair: deleteField(),
            serviceStatus: "กำลังดำเนินการ",
            updatedAt: new Date(),
            updatedBy: currentUser.displayName,
          });
          swal("สำเร็จ", "ลบข้อมูลเรียบร้อย", "success");
        } catch (error) {
          console.error("Error deleting external repair:", error);
          swal("Error", "Failed to delete external repair data", "error");
        }
      }
    });
  };

  const handleExportExternalRepair = async (ticketId) => {
    try {
      // Find the ticket
      const ticket = externalRepairList.find((item) => item.id === ticketId);
      if (!ticket) {
        swal("Error", "Ticket not found!", "error");
        return;
      }

      // Get patient information
      const patientRef = doc(db, "patients", ticket.hn);
      const patientInfo = await getDoc(patientRef);

      // External repair template URL
      const templateUrl =
        "https://firebasestorage.googleapis.com/v0/b/sleepy-9b23f.firebasestorage.app/o/templates%2FF-501-020%20External%20Repair%20Service%20Form.pdf?alt=media&token=5bd57e7e-6904-4968-a073-b7ed8d14a15f";

      // Load PDF template
      const existingPdfBytes = await fetch(templateUrl).then((res) =>
        res.arrayBuffer(),
      );

      // Load font
      const fonturl = "./assets/fonts/Sarabun-Medium.ttf";
      const fontBytes = await fetch(fonturl).then((res) => res.arrayBuffer());

      // Create PDF document
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      // Register font
      pdfDoc.registerFontkit(fontkit);
      const customFont = await pdfDoc.embedFont(fontBytes, { subset: true });

      // Get page and dimensions
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { width, height } = firstPage.getSize();

      // Embed images if available
      if (ticket.image && ticket.image.length > 0) {
        let imageY = height - 350; // Starting Y position for images
        const imageMaxWidth = 150;
        const imageMaxHeight = 100;

        for (const imageName of ticket.image) {
          try {
            const imageUrl = await getDownloadURL(
              ref(storage, `serviceImage/${ticket.id}/${imageName}`),
            );

            const imageResponse = await fetch(imageUrl);
            const imageBytes = await imageResponse.arrayBuffer();

            // Determine image type and embed
            let image;
            if (imageName.toLowerCase().endsWith(".png")) {
              image = await pdfDoc.embedPng(imageBytes);
            } else {
              image = await pdfDoc.embedJpg(imageBytes);
            }

            // Calculate scaled dimensions to fit within max bounds
            const scale = Math.min(
              imageMaxWidth / image.width,
              imageMaxHeight / image.height,
              1,
            );
            const scaledWidth = image.width * scale;
            const scaledHeight = image.height * scale;

            // Draw image
            firstPage.drawImage(image, {
              x: 180,
              y: imageY - scaledHeight,
              width: scaledWidth,
              height: scaledHeight,
            });

            imageY -= scaledHeight + 20; // Move down for next image
          } catch (error) {
            console.warn(`Failed to embed image ${imageName}:`, error);
          }
        }
      }

      // Fill PDF fields with ticket data
      // Service Order Number
      firstPage.drawText(ticket.serviceOrderNumber || "", {
        x: 450,
        y: height - 195,
        size: 10,
        font: customFont,
      });

      // External Repair Service No.
      if (ticket.externalRepair && ticket.externalRepair.externalRepairNo) {
        firstPage.drawText(ticket.externalRepair.externalRepairNo, {
          x: 450,
          y: height - 173,
          size: 10,
          font: customFont,
        });
      }

      // Date (sent date)
      if (ticket.externalRepair && ticket.externalRepair.sentDate) {
        const sentDate = ticket.externalRepair.sentDate.toDate
          ? ticket.externalRepair.sentDate.toDate()
          : new Date(ticket.externalRepair.sentDate);
        firstPage.drawText(
          sentDate.toLocaleDateString("th-TH", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }),
          {
            x: 180,
            y: height - 172,
            size: 10,
            font: customFont,
          },
        );
      }

      // Customer Information
      // firstPage.drawText(ticket.hn || "", {
      //   x: 180,
      //   y: height - 130,
      //   size: 10,
      //   font: customFont,
      // });

      // firstPage.drawText(
      //   (ticket.firstname || "") + " " + (ticket.lastname || ""),
      //   {
      //     x: 180,
      //     y: height - 147,
      //     size: 10,
      //     font: customFont,
      //   },
      // );

      // Mobile from patient data
      // const mobile = patientInfo.exists() ? patientInfo.data().mobile : "";
      // firstPage.drawText(mobile || "", {
      //   x: 180,
      //   y: height - 165,
      //   size: 10,
      //   font: customFont,
      // });

      // Equipment Information
      firstPage.drawText(ticket.brand || "", {
        x: 180,
        y: height - 215,
        size: 10,
        font: customFont,
      });

      firstPage.drawText(ticket.model || "", {
        x: 450,
        y: height - 217,
        size: 10,
        font: customFont,
      });

      firstPage.drawText(ticket.sn || "", {
        x: 180,
        y: height - 237,
        size: 10,
        font: customFont,
      });

      // Service Type
      if (ticket.serviceType) {
        firstPage.drawText(ticket.serviceType, {
          x: 180,
          y: height - 195,
          size: 10,
          font: customFont,
        });
      }

      // Accessories
      if (ticket.accessories) {
        firstPage.drawText(ticket.accessories, {
          x: 180,
          y: height - 260,
          size: 10,
          font: customFont,
          maxWidth: 350,
        });
      }

      // Images (list filenames)
      // if (ticket.image && ticket.image.length > 0) {
      //   const imageList = ticket.image.join(", ");
      //   firstPage.drawText(imageList, {
      //     x: 180,
      //     y: height - 280,
      //     size: 9,
      //     font: customFont,
      //     maxWidth: 350,
      //   });
      // }

      // Vendor
      if (ticket.externalRepair && ticket.externalRepair.vendor) {
        firstPage.drawText(ticket.externalRepair.vendor, {
          x: 180,
          y: height - 152,
          size: 10,
          font: customFont,
        });
      }

      // External Repair Details
      if (ticket.externalRepair && ticket.externalRepair.details) {
        firstPage.drawText(ticket.externalRepair.details, {
          x: 180,
          y: height - 300,
          size: 10,
          font: customFont,
          maxWidth: 350,
        });
      }

      // Notes
      if (ticket.externalRepair && ticket.externalRepair.notes) {
        firstPage.drawText(ticket.externalRepair.notes, {
          x: 100,
          y: height - 600,
          size: 9,
          font: customFont,
          maxWidth: 350,
        });
      }

      // Repair Status
      // if (ticket.externalRepair && ticket.externalRepair.repairStatus) {
      //   firstPage.drawText(ticket.externalRepair.repairStatus, {
      //     x: 180,
      //     y: height - 662,
      //     size: 10,
      //     font: customFont,
      //   });
      // }

      // Generate and Open PDF
      const pdfDocBytes = await pdfDoc.save();
      const pdfUrl = URL.createObjectURL(
        new Blob([pdfDocBytes], { type: "application/pdf" }),
      );
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Error exporting external repair PDF:", error);
      swal("Error", "Failed to export PDF. Please try again.", "error");
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="External Repair List" motherMenu="Service" />

      {/* Edit Modal */}
      <Modal
        className="fade bd-example-modal-lg"
        show={showModal}
        onHide={handleOnHide}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>แก้ไขข้อมูลส่งซ่อมบริษัทนอก</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editTicket && (
            <div className="mb-3 p-3 bg-light rounded">
              <p className="mb-1">
                <strong>Service Order:</strong> {editTicket.serviceOrderNumber}
              </p>
              <p className="mb-1">
                <strong>Customer:</strong> {editTicket.firstname}{" "}
                {editTicket.lastname}
              </p>
              <p className="mb-0">
                <strong>Equipment:</strong> {editTicket.brand}{" "}
                {editTicket.model} (SN: {editTicket.sn})
              </p>
            </div>
          )}

          {/* Service Type from ticket */}
          {editTicket && editTicket.serviceType && (
            <div className="form-group row m-1">
              <label className="col-lg-4 col-form-label">ประเภท</label>
              <div className="col-lg-6">
                <input
                  type="text"
                  className="form-control"
                  value={editTicket.serviceType}
                  disabled
                />
              </div>
            </div>
          )}

          {/* Accessories from ticket */}
          {editTicket && editTicket.accessories && (
            <div className="form-group row m-1">
              <label className="col-lg-4 col-form-label">รายการอุปกรณ์</label>
              <div className="col-lg-6">
                <textarea
                  className="form-control"
                  value={editTicket.accessories}
                  disabled
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* Images from ticket */}
          {editTicket && editTicket.image && editTicket.image.length > 0 && (
            <div className="form-group row m-1">
              <label className="col-lg-4 col-form-label">รูปภาพ</label>
              <div className="col-lg-6">
                {editTicket.image.map((img, idx) => (
                  <Link
                    key={idx}
                    to="#"
                    onClick={() => handleOnViewFile(editTicket.id, img)}
                    className="mr-2 d-block"
                  >
                    <i className="fa fa-image"></i> {img}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <ExternalRepairForm
            onChange={handleChange}
            initialValues={editTicket && editTicket.externalRepair}
          />
        </Modal.Body>
        <div className="d-flex justify-content-center mb-3">
          <Button className="sm" onClick={handleSubmit}>
            บันทึก
          </Button>
          <Button className="sm ml-2" variant="light" onClick={handleOnHide}>
            ยกเลิก
          </Button>
        </div>
      </Modal>

      {/* Image View Modal */}
      {!isImageLoading && (
        <Modal
          className="fade bd-example-modal-lg"
          show={imageModal}
          onHide={handleCloseImageModal}
          size="xl"
          backdrop={true}
          keyboard={true}
        >
          <Modal.Body className="m-0 p-0">
            <div
              className="card-body mx-0 p-0 col-sm-12"
              style={{ height: "80vh" }}
            >
              {fileUrl && (
                <img
                  src={fileUrl}
                  alt="Ticket Image"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          </Modal.Body>
        </Modal>
      )}

      <div className="form-head d-flex mb-3 mb-lg-5 align-items-start">
        <div className="mr-auto">
          <h2 className="text-black font-w600 mb-0">External Repair</h2>
        </div>
        <div className="input-group search-area d-inline-flex">
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            ref={searchRef}
            onChange={onSearchChange}
          />
          <div className="input-group-append">
            <span className="input-group-text c-pointer">
              <i className="flaticon-381-search-2"></i>
            </span>
          </div>
        </div>
      </div>

      <ExternalRepairItem
        tickets={externalRepairList}
        onEdit={handleOnEdit}
        onDelete={handleOnDelete}
        onExport={handleExportExternalRepair}
        totalItems={totalItems}
        totalPages={totalPages}
        currentPage={currentPage}
        itemsPerPage={itemPerPage}
        goToPage={goToPage}
        isStart={isStart}
        isEnd={isEnd}
        getNext={getNext}
        getPrev={getPrev}
      />
    </Fragment>
  );
};

export default ExternalRepairList;
